import {
  BadgeEstadoColada,
  FechaCR,
  ModalCrearPedidoBC,
  PickerObras,
  TablaBatchesDetalle,
} from '@/components/dominio';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useBatchesDetalle } from '@/hooks/useBatchesDetalle';
import {
  useActualizarM3Manual,
  useAgregarBatch,
  useAnularColada,
  useAsignarObra,
  useBatchesHuerfanos,
  useBomColada,
  useCerrarColada,
  useColada,
  useConfirmarColada,
  useDesanularColada,
  useDesconfirmarColada,
  useDesmarcarDigitada,
  useExcluirBatch,
  useGuardarBomOverride,
  useMarcarDigitada,
  useRestaurarBatch,
} from '@/hooks/useColadas';
import { useGuardarMapeoReceta, useProductosEnsambladosBC } from '@/hooks/useMapeoRecetas';
import { esAdmin, puedeMapearRecetas, puedeOperarColadas } from '@/lib/cliente';
import { nombrePlantaDisplay } from '@/lib/plantas';
import { cn, normalizarEntradaDecimal } from '@/lib/utils';
import type { ColadaDetalle, EstadoColada } from '@adelante/shared';
import {
  AlertTriangle,
  ArrowLeft,
  Ban,
  Building2,
  Check,
  Clock,
  FileText,
  Minus,
  Pencil,
  Plus,
  Send,
  Undo2,
  X,
  XCircle,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

/**
 * `/coladas/:id` — detalle completo de una colada.
 *
 *   - Header: código interno + badge de estado + metadatos.
 *   - Barra de acciones: botones de transición habilitados por estado.
 *   - Tabs: Batches | Líneas Pedido Ensamblado | Cilindros.
 *
 * Las acciones disparan mutaciones que invalidan el cache y refrescan
 * automáticamente la vista vía TanStack Query.
 */

// Helper: vuelve al listado preservando los filtros si entramos desde ahí.
// Si la app cargó directamente en /coladas/:id (link compartido, F5), no
// hay `state.from` y caemos a `/coladas` limpio.
function useVolverAListado() {
  const navegar = useNavigate();
  const location = useLocation();
  return () => {
    const from = (location.state as { from?: string } | null)?.from;
    navegar(from ?? '/coladas');
  };
}

export function ColadaDetallePantalla() {
  const params = useParams<{ id: string }>();
  const volverAListado = useVolverAListado();
  const idNum = params.id ? Number(params.id) : null;
  const idValido = idNum !== null && Number.isInteger(idNum) && idNum > 0;
  const id = idValido ? idNum : null;

  const consulta = useColada(id);

  if (consulta.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-80" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (consulta.error || !consulta.data?.data) {
    return (
      <div className="space-y-4">
        <Button variant="outline" size="sm" onClick={volverAListado}>
          <ArrowLeft className="h-4 w-4" /> Volver al listado
        </Button>
        <p className="text-sm text-destructive">
          No se pudo cargar la colada {params.id}: {consulta.error?.message ?? 'no encontrada'}
        </p>
      </div>
    );
  }

  return <Contenido detalle={consulta.data.data} />;
}

// =============================================================================
// Contenido (extraído para tener `detalle` no-null garantizado)
// =============================================================================

function Contenido({ detalle }: { detalle: ColadaDetalle }) {
  const volverAListado = useVolverAListado();
  const { colada, batches, lineas_pedido_ensamblado } = detalle;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={volverAListado}>
          <ArrowLeft className="h-4 w-4" /> Volver
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">
          Colada <span className="font-mono">#{colada.codigo_interno}</span>
        </h1>
        <BadgeEstadoColada estado={colada.estado} motivoAnulacion={colada.motivo_anulacion} />
        {colada.origen_registro === 'manual' && (
          <Badge variant="outline" className="border-violet-300 bg-violet-100 text-violet-800">
            Registro manual
          </Badge>
        )}
      </div>

      {/* Si fue consolidada o anulada, mostramos el motivo en banner visible
          arriba de las acciones — no queremos que quede oculto detrás de
          un tooltip. */}
      {colada.estado === 'anulada' && colada.motivo_anulacion && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <div className="font-medium">Motivo de la anulación</div>
          <div className="mt-0.5 whitespace-pre-wrap">{colada.motivo_anulacion}</div>
        </div>
      )}

      {/* Estado de la solicitud de envío a BC (si existe). Pendiente/rechazada
          se muestran arriba de las acciones, igual que la anulación, para que
          no queden ocultas. Aprobada no necesita banner: ya se ve en "N°
          Pedido BC" del header. */}
      {detalle.solicitud_bc_pendiente?.estado === 'pendiente' && (
        <div className="flex items-center gap-2 rounded-md border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900">
          <Clock className="h-4 w-4 shrink-0" />
          <span>
            Envío a BC pendiente de autorización — solicitado por{' '}
            {detalle.solicitud_bc_pendiente.solicitado_por_email ?? 'desconocido'} el{' '}
            <FechaCR fecha={detalle.solicitud_bc_pendiente.fecha_solicitud} formato="corto" />.
          </span>
        </div>
      )}
      {detalle.solicitud_bc_pendiente?.estado === 'rechazada' && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
          <div className="flex items-center gap-2 font-medium">
            <XCircle className="h-4 w-4 shrink-0" /> Envío a BC rechazado
          </div>
          <div className="mt-0.5 whitespace-pre-wrap">
            {detalle.solicitud_bc_pendiente.motivo_rechazo}
          </div>
        </div>
      )}

      <BarraAcciones detalle={detalle} />

      <HeaderColada detalle={detalle} />

      {/* Una colada manual (Fiori) no tiene batches del dosificador — esos
          tabs no aplican. Arranca directo en "Líneas Pedido BC". */}
      <Tabs defaultValue={colada.origen_registro === 'manual' ? 'lineas' : 'batches'}>
        <TabsList>
          {colada.origen_registro !== 'manual' && (
            <>
              <TabsTrigger value="batches">Batches ({batches.length})</TabsTrigger>
              <TabsTrigger value="datos-planta">Datos de planta</TabsTrigger>
            </>
          )}
          <TabsTrigger value="lineas">
            Líneas Pedido BC ({lineas_pedido_ensamblado.length})
          </TabsTrigger>
          <TabsTrigger value="cilindros">Cilindros (0)</TabsTrigger>
        </TabsList>

        {colada.origen_registro !== 'manual' && (
          <>
            <TabsContent value="batches" className="mt-4">
              <TablaBatches batches={batches} colada={colada} />
            </TabsContent>

            <TabsContent value="datos-planta" className="mt-4">
              <DatosPlantaColada idColada={colada.id_colada} />
            </TabsContent>
          </>
        )}

        <TabsContent value="lineas" className="mt-4">
          {colada.origen_registro === 'manual' ? (
            <TablaBomBC
              idColada={colada.id_colada}
              codigoProducto={colada.codigo_receta_bc}
              puedeEditar={puedeOperarColadas() && !colada.numero_pedido_ensamblado_bc}
            />
          ) : (
            <TablaLineasBc detalle={detalle} />
          )}
        </TabsContent>

        <TabsContent value="cilindros" className="mt-4">
          <TablaCilindros />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// =============================================================================
// Tab "Datos de planta" — tabla configurable de batches con datos crudos
// =============================================================================

function DatosPlantaColada({ idColada }: { idColada: number }) {
  // Traemos hasta 500 batches de esta colada (las coladas tienen pocos).
  const consulta = useBatchesDetalle({ id_colada: idColada, por_pagina: 500 });
  const batches = consulta.data?.data?.batches ?? [];

  return (
    <Card>
      <CardContent className="p-3">
        <p className="mb-3 text-xs text-muted-foreground">
          Datos crudos reportados por la planta para cada batch de esta colada. Útil para revisar
          exceso de agua, humedad de áridos o desviación de aditivos cuando una muestra de
          laboratorio no rinde lo esperado. Configurá las columnas con el botón "Columnas".
        </p>
        <TablaBatchesDetalle batches={batches} cargando={consulta.isLoading} />
        {consulta.error && (
          <p className="mt-2 text-sm text-destructive">
            Error al cargar datos de planta: {consulta.error.message}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Barra de acciones con botones de transición
// =============================================================================

function BarraAcciones({ detalle }: { detalle: ColadaDetalle }) {
  const { colada, solicitud_bc_pendiente: solicitud } = detalle;
  const [modal, setModal] = useState<'digitar' | 'anular' | 'obra' | 'pedido-bc' | null>(null);

  const confirmar = useConfirmarColada();
  const desconfirmar = useDesconfirmarColada();
  const marcarDigitada = useMarcarDigitada();
  const desmarcarDigitada = useDesmarcarDigitada();
  const cerrar = useCerrarColada();
  const anular = useAnularColada();
  const desanular = useDesanularColada();
  const asignarObra = useAsignarObra();

  const id = colada.id_colada;
  const estado: EstadoColada = colada.estado;

  // Anular/desanular son acciones de Admin (el backend exige ROLES_ADMIN).
  // Ocultamos los botones para no-Admin (Operador / Ingeniería) en vez de
  // mostrarlos y que el click reciba un 403.
  const puedeAdministrar = esAdmin();
  // Día a día operativo (confirmar/digitar/cerrar/asignar obra/solicitar
  // envío a BC): Admin y Operador (ROLES_OPERATIVOS del API). Ingeniería
  // quedó afuera a propósito — autoriza esos envíos, no los solicita.
  const puedeOperar = puedeOperarColadas();

  // Quién puede transicionar desde cada estado.
  const puede = {
    confirmar: puedeOperar && estado === 'sugerida',
    // Una colada manual nace directo en 'confirmada' — no hay "sugerida" a
    // la que volver (no hubo agrupador automático que revisar).
    desconfirmar: puedeOperar && estado === 'confirmada' && colada.origen_registro !== 'manual',
    // Solicitar envío a BC: si la colada todavía no tiene N° BC asignado
    // NI una solicitud pendiente (evita duplicar solicitudes — el backend
    // igual lo rechazaría con 409 SOLICITUD_YA_PENDIENTE).
    // Aceptamos confirmada (flujo normal) y cerrada (regularizar coladas
    // históricas que se cerraron sin pasar por BC — caso del cierre masivo
    // de abril/2026 donde se digitó por planilla pero no se generó pedido
    // ensamblado, y ahora hay que llenarles el campo retroactivamente).
    solicitarPedidoBC:
      puedeOperar &&
      (estado === 'confirmada' || estado === 'cerrada') &&
      !colada.numero_pedido_ensamblado_bc &&
      solicitud?.estado !== 'pendiente',
    marcarDigitada: puedeOperar && estado === 'confirmada',
    desmarcarDigitada: puedeOperar && estado === 'digitada',
    cerrar: puedeOperar && estado === 'digitada',
    anular:
      puedeAdministrar &&
      (estado === 'sugerida' || estado === 'confirmada' || estado === 'digitada'),
    // Desanular: safety net para deshacer una anulación. Solo Admin (validado
    // también en el backend). Vuelve a 'sugerida' — el operador re-ejecuta
    // el flujo desde ahí si necesita digitar/cerrar de nuevo.
    desanular: puedeAdministrar && estado === 'anulada',
    // Permitir cambiar obra incluso en cerrada (metadata: no afecta números
    // ni N° BC). Útil para histórico que pasó a cerrada sin obra asignada.
    // Solo 'anulada' queda bloqueada.
    asignarObra: puedeOperar && estado !== 'anulada',
  };

  const algunaPendiente =
    confirmar.isPending ||
    desconfirmar.isPending ||
    marcarDigitada.isPending ||
    desmarcarDigitada.isPending ||
    cerrar.isPending ||
    anular.isPending ||
    desanular.isPending ||
    asignarObra.isPending;

  return (
    <>
      <Card>
        <CardContent className="flex flex-wrap items-center gap-2 p-4">
          {puede.confirmar && (
            <Button
              size="sm"
              onClick={() => confirmar.mutate({ id })}
              disabled={algunaPendiente}
              className="gap-1.5"
            >
              <Check className="h-4 w-4" /> Confirmar
            </Button>
          )}
          {puede.desconfirmar && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => desconfirmar.mutate({ id })}
              disabled={algunaPendiente}
              className="gap-1.5"
            >
              <Undo2 className="h-4 w-4" /> Volver a sugerida
            </Button>
          )}
          {puede.solicitarPedidoBC && (
            <Button
              size="sm"
              onClick={() => setModal('pedido-bc')}
              disabled={algunaPendiente}
              className="gap-1.5"
              title="Solicitar el envío a BC — un Ingeniero de Proyecto debe autorizarlo antes de que viaje a Business Central"
            >
              <Send className="h-4 w-4" /> Solicitar envío a BC
            </Button>
          )}
          {puede.marcarDigitada && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setModal('digitar')}
              disabled={algunaPendiente}
              className="gap-1.5"
              title="Digitar manualmente el N° de pedido (cuando se creó fuera de la app)"
            >
              <Send className="h-4 w-4" /> Digitar N° manual
            </Button>
          )}
          {puede.desmarcarDigitada && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => desmarcarDigitada.mutate({ id })}
              disabled={algunaPendiente}
              className="gap-1.5"
            >
              <Undo2 className="h-4 w-4" /> Volver a confirmada
            </Button>
          )}
          {puede.cerrar && (
            <Button
              size="sm"
              onClick={() => cerrar.mutate({ id })}
              disabled={algunaPendiente}
              className="gap-1.5"
            >
              <Check className="h-4 w-4" /> Cerrar
            </Button>
          )}
          {puede.anular && (
            <Button
              size="sm"
              variant="destructive"
              onClick={() => setModal('anular')}
              disabled={algunaPendiente}
              className="gap-1.5"
            >
              <Ban className="h-4 w-4" /> Anular
            </Button>
          )}
          {puede.desanular && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (
                  confirm(
                    `¿Desanular colada #${colada.codigo_interno}?\n\nVuelve a estado "sugerida". Si tenía número de Pedido BC, NO se restaura: el operador debe re-digitarlo si corresponde.`,
                  )
                ) {
                  desanular.mutate({ id });
                }
              }}
              disabled={algunaPendiente}
              className="gap-1.5"
            >
              <Undo2 className="h-4 w-4" /> Desanular
            </Button>
          )}
          {puede.asignarObra && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setModal('obra')}
              disabled={algunaPendiente}
              className="gap-1.5"
            >
              <Building2 className="h-4 w-4" />
              {colada.obra_works_no ? 'Cambiar obra' : 'Asignar obra'}
            </Button>
          )}
          {!puede.confirmar &&
            !puede.desconfirmar &&
            !puede.marcarDigitada &&
            !puede.desmarcarDigitada &&
            !puede.cerrar &&
            !puede.anular &&
            !puede.desanular &&
            !puede.asignarObra && (
              <p className="text-sm text-muted-foreground">
                Sin acciones disponibles para estado "{estado}".
              </p>
            )}
        </CardContent>
      </Card>

      <ModalMarcarDigitada
        abierto={modal === 'digitar'}
        cerrar={() => setModal(null)}
        onConfirmar={(num, obra) =>
          marcarDigitada.mutate(
            { id, numero_pedido_ensamblado_bc: num, obra_works_no: obra ?? undefined },
            { onSuccess: () => setModal(null) },
          )
        }
        cargando={marcarDigitada.isPending}
      />
      <ModalAnular
        abierto={modal === 'anular'}
        cerrar={() => setModal(null)}
        onConfirmar={(motivo) =>
          anular.mutate({ id, motivo_anulacion: motivo }, { onSuccess: () => setModal(null) })
        }
        cargando={anular.isPending}
      />
      <ModalAsignarObra
        abierto={modal === 'obra'}
        cerrar={() => setModal(null)}
        valorActual={colada.obra_works_no}
        onConfirmar={(obra) =>
          asignarObra.mutate({ id, obra_works_no: obra }, { onSuccess: () => setModal(null) })
        }
        cargando={asignarObra.isPending}
      />
      <ModalCrearPedidoBC
        abierto={modal === 'pedido-bc'}
        cerrar={() => setModal(null)}
        idColada={id}
      />
    </>
  );
}

// =============================================================================
// Header de metadatos
// =============================================================================

function HeaderColada({ detalle }: { detalle: ColadaDetalle }) {
  const c = detalle.colada;
  return (
    <Card>
      <CardContent className="grid gap-4 p-6 md:grid-cols-3 lg:grid-cols-4">
        <Campo
          titulo="Planta"
          valor={nombrePlantaDisplay(c.planta_nombre)}
          subtitulo={c.planta_serial}
        />
        <Campo
          titulo="Receta Blend"
          valor={c.receta_blend_nombre}
          subtitulo={
            <SelectorRecetaBC idRecetaBlend={c.id_receta_blend} codigoActual={c.codigo_receta_bc} />
          }
          alerta={!c.codigo_receta_bc}
        />
        <Campo
          titulo="Destino"
          valor={c.destino_display}
          subtitulo={
            c.nombre_canonico && c.destino_raw !== c.nombre_canonico
              ? `digitado: ${c.destino_raw}`
              : undefined
          }
        />
        <Campo
          titulo="m³ producidos"
          valor={
            c.origen_registro === 'manual' && !c.numero_pedido_ensamblado_bc ? (
              <CampoM3Editable idColada={c.id_colada} valorActual={c.m3_producidos} />
            ) : (
              c.m3_producidos.toFixed(3)
            )
          }
          subtitulo={
            c.origen_registro === 'manual' ? 'registro manual' : `${c.cantidad_batches} batch(es)`
          }
          mono
        />
        {c.actividad_nombre && <Campo titulo="Actividad" valor={c.actividad_nombre} />}
        <Campo
          titulo="Fecha"
          valor={<FechaCR fecha={c.fecha_inicio} formato="corto" />}
          subtitulo={
            <span>
              de <FechaCR fecha={c.fecha_inicio} formato="corto" /> a{' '}
              <FechaCR fecha={c.fecha_fin} formato="corto" />
            </span>
          }
        />
        <Campo
          titulo="a/c promedio"
          valor={
            c.relacion_agua_cemento_promedio === null
              ? '—'
              : c.relacion_agua_cemento_promedio.toFixed(4)
          }
          subtitulo={
            c.fc_teorica_kg_cm2 ? `f'c teórica: ${c.fc_teorica_kg_cm2} kg/cm²` : "sin f'c teórica"
          }
          mono
        />
        <Campo
          titulo="Alarmas"
          valor={c.cantidad_alarmas_total.toString()}
          alerta={c.cantidad_alarmas_total > 0}
          mono
        />
        <Campo titulo="N° Pedido BC" valor={c.numero_pedido_ensamblado_bc ?? '—'} mono />
        <Campo
          titulo="Obra"
          valor={c.obra_works_no ?? '—'}
          subtitulo={c.obra_display_name ?? undefined}
          mono
        />
        {c.observaciones && (
          <div className="md:col-span-3 lg:col-span-4">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              Descripción
            </div>
            <div className="mt-1 whitespace-pre-wrap text-sm">{c.observaciones}</div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Edición inline de los m³ de una colada manual (Fiori). Solo se renderiza
 * mientras la colada no tenga N° de pedido BC asignado — el backend
 * también lo valida (409 si ya se envió). El chofer estima los m³ en el
 * celular; esto deja corregirlo antes de solicitar el envío a BC.
 */
function CampoM3Editable(props: { idColada: number; valorActual: number }) {
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState(String(props.valorActual));
  const actualizar = useActualizarM3Manual();

  if (!puedeOperarColadas()) return <>{props.valorActual.toFixed(3)}</>;

  if (!editando) {
    return (
      <button
        type="button"
        onClick={() => {
          setValor(String(props.valorActual));
          setEditando(true);
        }}
        className="inline-flex items-center gap-1 underline decoration-dotted underline-offset-2 hover:text-foreground"
      >
        {props.valorActual.toFixed(3)} <Pencil className="h-3 w-3" />
      </button>
    );
  }

  const guardar = () => {
    const n = Number(valor);
    if (!Number.isFinite(n) || n <= 0) return;
    actualizar.mutate(
      { id: props.idColada, m3_producidos: n },
      { onSuccess: () => setEditando(false) },
    );
  };

  return (
    <span className="inline-flex items-center gap-1">
      <Input
        type="text"
        inputMode="decimal"
        value={valor}
        onChange={(e) => setValor(normalizarEntradaDecimal(e.target.value))}
        className="h-7 w-24 font-mono text-sm"
        autoFocus
      />
      <button
        type="button"
        onClick={guardar}
        disabled={actualizar.isPending}
        className="text-emerald-600 hover:text-emerald-700"
        title="Guardar"
      >
        <Check className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => setEditando(false)}
        className="text-muted-foreground hover:text-foreground"
        title="Cancelar"
      >
        <X className="h-4 w-4" />
      </button>
    </span>
  );
}

/**
 * Muestra el estado del mapeo Receta Blend → BC y, si el usuario tiene
 * permiso (Admin/Ingeniería/Operador), deja corregirlo sin salir de la
 * colada — antes solo se podía arreglar desde `/admin/mapeo-recetas`. Al
 * guardar, el backend hace backfill y esta colada (si sigue
 * sugerida/confirmada) queda mapeada de inmediato.
 */
function SelectorRecetaBC(props: { idRecetaBlend: number; codigoActual: string | null }) {
  const puedeEditar = puedeMapearRecetas();
  const [editando, setEditando] = useState(false);
  const consultaProductos = useProductosEnsambladosBC();
  const guardar = useGuardarMapeoReceta();
  const productos = consultaProductos.data?.data?.productos ?? [];

  if (!puedeEditar) {
    return <>{props.codigoActual ? `BC: ${props.codigoActual}` : 'sin mapeo a BC'}</>;
  }

  if (!editando) {
    return (
      <button
        type="button"
        onClick={() => setEditando(true)}
        className="underline decoration-dotted underline-offset-2 hover:text-foreground"
      >
        {props.codigoActual ? `BC: ${props.codigoActual} (cambiar)` : 'sin mapeo a BC — mapear'}
      </button>
    );
  }

  return (
    <Select
      value={props.codigoActual ?? ''}
      onValueChange={(v) => {
        const producto = productos.find((p) => p.codigo === v);
        if (!producto) return;
        guardar.mutate(
          {
            id_receta_blend: props.idRecetaBlend,
            codigo_bc: producto.codigo,
            descripcion_bc: producto.descripcion,
          },
          { onSuccess: () => setEditando(false) },
        );
      }}
      disabled={guardar.isPending}
      open
      onOpenChange={(abierto) => {
        if (!abierto) setEditando(false);
      }}
    >
      <SelectTrigger className="h-6 w-[220px] text-xs">
        <SelectValue placeholder="Elegir producto BC..." />
      </SelectTrigger>
      <SelectContent>
        {productos.map((p) => (
          <SelectItem key={p.codigo} value={p.codigo}>
            {p.codigo} — {p.descripcion}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function Campo(props: {
  titulo: string;
  valor: React.ReactNode;
  subtitulo?: React.ReactNode;
  mono?: boolean;
  alerta?: boolean;
}) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{props.titulo}</div>
      <div
        className={cn(
          'mt-1 text-base font-semibold',
          props.mono && 'font-mono tabular-nums',
          props.alerta && 'text-destructive',
        )}
      >
        {props.valor}
      </div>
      {props.subtitulo && (
        <div className="mt-0.5 text-xs text-muted-foreground">{props.subtitulo}</div>
      )}
    </div>
  );
}

// =============================================================================
// Tabs
// =============================================================================

function TablaBatches({
  batches,
  colada,
}: {
  batches: ColadaDetalle['batches'];
  colada: ColadaDetalle['colada'];
}) {
  const [batchAExcluir, setBatchAExcluir] = useState<{ id: number; record: number } | null>(null);
  const [modalHuerfanos, setModalHuerfanos] = useState(false);
  const excluir = useExcluirBatch();
  const agregar = useAgregarBatch();
  const restaurar = useRestaurarBatch();

  // Excluir/agregar/restaurar batches es operativo (Admin/Operador —
  // Ingeniería no). Excluir solo en sugerida/confirmada. Agregar huérfano
  // solo en sugerida.
  const puedeOperar = puedeOperarColadas();
  const puedeExcluir =
    puedeOperar && (colada.estado === 'sugerida' || colada.estado === 'confirmada');
  const puedeAgregarHuerfano = puedeOperar && colada.estado === 'sugerida';

  return (
    <>
      {puedeAgregarHuerfano && (
        <div className="mb-3 flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setModalHuerfanos(true)}
            className="gap-1.5"
          >
            <Plus className="h-4 w-4" /> Agregar batch huérfano
          </Button>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Record</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead className="text-right">m³</TableHead>
                <TableHead className="text-right">a/c real</TableHead>
                <TableHead className="text-right">Alarmas</TableHead>
                <TableHead>Excluido</TableHead>
                {puedeExcluir && <TableHead />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {batches.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={puedeExcluir ? 7 : 6}
                    className="py-10 text-center text-sm text-muted-foreground"
                  >
                    Sin batches vinculados.
                  </TableCell>
                </TableRow>
              )}
              {batches.map((b) => (
                <TableRow key={b.id_batch} className={cn(b.excluido && 'opacity-50')}>
                  <TableCell className="font-mono">{b.record_no}</TableCell>
                  <TableCell className="font-mono text-sm">
                    <FechaCR fecha={b.fecha_inicio} formato="corto" />
                  </TableCell>
                  <TableCell className="text-right font-mono tabular-nums">
                    {b.m3_producidos.toFixed(3)}
                  </TableCell>
                  <TableCell className="text-right font-mono tabular-nums">
                    {b.ac_real === null ? '—' : b.ac_real.toFixed(4)}
                  </TableCell>
                  <TableCell
                    className={cn(
                      'text-right font-mono tabular-nums',
                      b.cantidad_alarmas > 0 && 'text-destructive font-semibold',
                    )}
                  >
                    {b.cantidad_alarmas}
                  </TableCell>
                  <TableCell>
                    {b.excluido ? (
                      <span className="text-sm text-muted-foreground">
                        Sí{b.excluido_motivo ? `: ${b.excluido_motivo}` : ''}
                      </span>
                    ) : (
                      <span className="text-sm text-muted-foreground">No</span>
                    )}
                  </TableCell>
                  {puedeExcluir && (
                    <TableCell className="text-right">
                      {!b.excluido && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setBatchAExcluir({ id: b.id_batch, record: b.record_no })}
                          disabled={excluir.isPending}
                          className="gap-1.5 text-destructive hover:text-destructive"
                        >
                          <Minus className="h-4 w-4" /> Excluir
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <ModalExcluirBatch
        info={batchAExcluir}
        cerrar={() => setBatchAExcluir(null)}
        onConfirmar={(motivo) =>
          batchAExcluir &&
          excluir.mutate(
            { idColada: colada.id_colada, idBatch: batchAExcluir.id, motivo },
            { onSuccess: () => setBatchAExcluir(null) },
          )
        }
        cargando={excluir.isPending}
      />

      <ModalAgregarHuerfano
        abierto={modalHuerfanos}
        cerrar={() => setModalHuerfanos(false)}
        onConfirmar={(idBatch) =>
          agregar.mutate(
            { idColadaDestino: colada.id_colada, idBatch },
            { onSuccess: () => setModalHuerfanos(false) },
          )
        }
        onRestaurar={(idBatch) =>
          restaurar.mutate(
            { idColada: colada.id_colada, idBatch },
            { onSuccess: () => setModalHuerfanos(false) },
          )
        }
        cargando={agregar.isPending || restaurar.isPending}
        idColadaActual={colada.id_colada}
      />
    </>
  );
}

/**
 * Composición real de un producto de BC (BOM: materiales + recurso, cada
 * uno con su "cantidad por" unidad del producto) multiplicada por los m³
 * de la colada. Reemplaza a `TablaLineasBc` en coladas manuales (Fiori):
 * ahí no hay batches de los que derivar consumo real, así que la única
 * forma de saber qué se rebaja de inventario es leer la fórmula del
 * producto directo desde BC — ver `bc-bom-dominio.ts` / diagnóstico
 * `GET /api/diagnostico/bc/bom/{codigo}`.
 *
 * `puedeEditar` (Operador, y solo mientras la colada no tenga ya un N° de
 * pedido BC) habilita corregir la cantidad por unidad de cada línea — un
 * override que aplica SOLO a esta colada, la BOM maestra en BC no se toca.
 * Al solicitar el envío a BC, esas correcciones se aplican como PATCH sobre
 * las líneas que BC autogenera (ver `ajustarLineasComponente`).
 */
function TablaBomBC({
  idColada,
  codigoProducto,
  puedeEditar,
}: {
  idColada: number;
  codigoProducto: string | null;
  puedeEditar: boolean;
}) {
  const consulta = useBomColada(codigoProducto ? idColada : null);

  if (!codigoProducto) {
    return (
      <Card>
        <CardContent className="p-10 text-center text-sm text-muted-foreground">
          No hay líneas para BC. Posible causa: la receta no está mapeada (sin Receta BC).
        </CardContent>
      </Card>
    );
  }

  if (consulta.isLoading) {
    return (
      <Card>
        <CardContent className="space-y-2 p-4">
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-6 w-1/3" />
        </CardContent>
      </Card>
    );
  }

  const errorMsg = consulta.data?.error?.mensaje ?? (consulta.error?.message || null);
  if (errorMsg) {
    return (
      <Card className="border-destructive/40 bg-destructive/5">
        <CardContent className="p-4 text-sm text-destructive">
          No se pudo leer la fórmula (BOM) de {codigoProducto} en Business Central: {errorMsg}
        </CardContent>
      </Card>
    );
  }

  const lineas = consulta.data?.data?.lineas ?? [];
  const m3 = consulta.data?.data?.m3_producidos ?? 0;
  if (lineas.length === 0) {
    return (
      <Card>
        <CardContent className="p-10 text-center text-sm text-muted-foreground">
          Business Central no devolvió una fórmula (BOM) para el producto{' '}
          <span className="font-mono">{codigoProducto}</span>.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          <FileText className="h-4 w-4" aria-hidden="true" />
          Composición de {codigoProducto} (BOM de BC) × {m3.toFixed(3)} m³
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tipo</TableHead>
              <TableHead>Código</TableHead>
              <TableHead>Descripción</TableHead>
              <TableHead>UM</TableHead>
              <TableHead className="text-right">Cant. por m³</TableHead>
              <TableHead className="text-right">Cantidad total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {lineas.map((l) => (
              <TableRow key={`${l.tipo}-${l.codigo}`}>
                <TableCell className="text-sm">
                  {l.tipo === 'recurso' ? 'Recurso' : 'Material'}
                </TableCell>
                <TableCell className="font-mono text-sm">{l.codigo}</TableCell>
                <TableCell className="text-sm">
                  {l.descripcion}
                  {l.override && (
                    <span
                      className="ml-1.5 rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800"
                      title="Cantidad corregida a mano para esta colada — la BOM en BC no cambió"
                    >
                      corregido
                    </span>
                  )}
                </TableCell>
                <TableCell className="font-mono text-sm">{l.unidad}</TableCell>
                <TableCell className="text-right font-mono tabular-nums">
                  {puedeEditar ? (
                    <CeldaCantidadBomEditable
                      idColada={idColada}
                      codigo={l.codigo}
                      valorActual={l.cantidad_por_unidad}
                    />
                  ) : (
                    l.cantidad_por_unidad.toFixed(4)
                  )}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums">
                  {l.cantidad_total.toFixed(4)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

/** Edición inline de "cantidad por m³" de una línea de BOM — ver `TablaBomBC`. */
function CeldaCantidadBomEditable(props: {
  idColada: number;
  codigo: string;
  valorActual: number;
}) {
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState(String(props.valorActual));
  const guardar = useGuardarBomOverride();

  if (!editando) {
    return (
      <button
        type="button"
        onClick={() => {
          setValor(String(props.valorActual));
          setEditando(true);
        }}
        className="inline-flex items-center gap-1 underline decoration-dotted underline-offset-2 hover:text-foreground"
      >
        {props.valorActual.toFixed(4)} <Pencil className="h-3 w-3" />
      </button>
    );
  }

  const confirmarGuardar = () => {
    const n = Number(valor);
    if (!Number.isFinite(n) || n < 0) return;
    guardar.mutate(
      { idColada: props.idColada, codigo: props.codigo, cantidad_por_unidad: n },
      { onSuccess: () => setEditando(false) },
    );
  };

  return (
    <span className="inline-flex items-center gap-1">
      <Input
        type="text"
        inputMode="decimal"
        value={valor}
        onChange={(e) => setValor(normalizarEntradaDecimal(e.target.value))}
        className="h-7 w-24 font-mono text-sm"
        autoFocus
      />
      <button
        type="button"
        onClick={confirmarGuardar}
        disabled={guardar.isPending}
        className="text-emerald-600 hover:text-emerald-700"
        title="Guardar"
      >
        <Check className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => setEditando(false)}
        className="text-muted-foreground hover:text-foreground"
        title="Cancelar"
      >
        <X className="h-4 w-4" />
      </button>
    </span>
  );
}

function TablaLineasBc({ detalle }: { detalle: ColadaDetalle }) {
  const lineas = detalle.lineas_pedido_ensamblado;
  if (lineas.length === 0) {
    return (
      <Card>
        <CardContent className="p-10 text-center text-sm text-muted-foreground">
          No hay líneas para BC. Posible causa: la receta no está mapeada (sin Receta BC).
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          <FileText className="h-4 w-4" aria-hidden="true" />
          Líneas para digitar en Pedido de Ensamblado
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Código BC</TableHead>
              <TableHead>Descripción</TableHead>
              <TableHead>UM</TableHead>
              <TableHead className="text-right">Cantidad</TableHead>
              <TableHead className="text-right">Ratio por m³</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {lineas.map((l) => (
              <TableRow key={l.codigo_bc}>
                <TableCell className="font-mono text-sm">{l.codigo_bc}</TableCell>
                <TableCell className="text-sm">{l.descripcion_bc}</TableCell>
                <TableCell className="font-mono text-sm">{l.um_bc}</TableCell>
                <TableCell className="text-right font-mono tabular-nums">
                  {l.cantidad_total_consumida.toFixed(4)}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums">
                  {l.ratio_por_m3.toFixed(4)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function TablaCilindros() {
  // Sesión 5: solo lectura placeholder. Los endpoints de registro de
  // cilindros llegan en una sesión posterior.
  return (
    <Card>
      <CardContent className="p-10 text-center text-sm text-muted-foreground">
        <AlertTriangle className="mx-auto mb-2 h-6 w-6" aria-hidden="true" />
        El registro de cilindros llega en una sesión posterior. Por ahora la tabla{' '}
        <span className="font-mono">hor.cilindros</span> está vacía.
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Modales
// =============================================================================

/**
 * Prefijo fijo del N° de Pedido de Ensamblado en Business Central. El
 * digitador solo tipea el resto numérico — la app concatena PENS al
 * guardar y, si pegan "PENS1234", quita el prefijo automáticamente.
 */
const PREFIJO_PEDIDO_BC = 'PENS';

function ModalMarcarDigitada(props: {
  abierto: boolean;
  cerrar: () => void;
  onConfirmar: (numero: string, obraWorksNo: string | null) => void;
  cargando: boolean;
}) {
  // Solo guardamos la parte numérica/sufijo: el prefijo "PENS" se concatena
  // al guardar. Si el usuario pega "PENS2285" se lo limpiamos automático.
  const [numero, setNumero] = useState('');
  const [obra, setObra] = useState<string | null>(null);
  const setNumeroLimpio = (v: string) => {
    const upper = v.toUpperCase();
    setNumero(upper.startsWith(PREFIJO_PEDIDO_BC) ? v.slice(PREFIJO_PEDIDO_BC.length) : v);
  };
  return (
    <Dialog
      open={props.abierto}
      onOpenChange={(o) => {
        if (!o) {
          setNumero('');
          setObra(null);
          props.cerrar();
        }
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Marcar como digitada</DialogTitle>
          <DialogDescription>
            Anotá el número de Pedido de Ensamblado generado en Business Central. Opcionalmente
            asociá la obra (podés agregarla después con el botón "Asignar obra").
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const limpio = numero.trim();
            if (limpio) props.onConfirmar(`${PREFIJO_PEDIDO_BC}${limpio}`, obra);
          }}
          className="grid gap-4"
        >
          <div className="grid gap-1.5">
            <Label htmlFor="num">Número de Pedido</Label>
            <div className="flex items-stretch overflow-hidden rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
              <span className="flex items-center bg-muted px-3 font-mono text-sm font-semibold text-muted-foreground">
                {PREFIJO_PEDIDO_BC}
              </span>
              <input
                id="num"
                value={numero}
                onChange={(e) => setNumeroLimpio(e.target.value)}
                placeholder="2285"
                // biome-ignore lint/a11y/noAutofocus: modal recién abierto, foco esperado
                autoFocus
                maxLength={50}
                required
                className="flex-1 bg-transparent px-3 py-2 font-mono text-sm outline-none"
              />
            </div>
          </div>

          <div className="grid gap-1.5 border-t pt-4">
            <Label>Obra (opcional)</Label>
            <PickerObras valor={obra} onChange={setObra} maxAltoPx={200} />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={props.cerrar}
              disabled={props.cargando}
            >
              <X className="h-4 w-4" /> Cancelar
            </Button>
            <Button type="submit" disabled={props.cargando || !numero.trim()}>
              <Send className="h-4 w-4" />
              {props.cargando ? 'Guardando…' : 'Marcar digitada'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ModalAnular(props: {
  abierto: boolean;
  cerrar: () => void;
  onConfirmar: (motivo: string) => void;
  cargando: boolean;
}) {
  const [motivo, setMotivo] = useState('');
  return (
    <Dialog
      open={props.abierto}
      onOpenChange={(o) => {
        if (!o) {
          setMotivo('');
          props.cerrar();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Anular colada</DialogTitle>
          <DialogDescription>
            Solo Admin puede deshacer esta acción vía "Desanular" (la colada vuelve a "sugerida",
            pero pierde el número de Pedido BC si tenía uno). La colada queda marcada como anulada
            con el motivo y el responsable.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const limpio = motivo.trim();
            if (limpio) props.onConfirmar(limpio);
          }}
          className="grid gap-3"
        >
          <Label htmlFor="motivo">Motivo de anulación</Label>
          <Textarea
            id="motivo"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ej: batch contaminado, rechazado en obra, error en agrupación..."
            rows={4}
            maxLength={2000}
            required
          />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={props.cerrar}
              disabled={props.cargando}
            >
              <X className="h-4 w-4" /> Cancelar
            </Button>
            <Button type="submit" variant="destructive" disabled={props.cargando || !motivo.trim()}>
              <Ban className="h-4 w-4" />
              {props.cargando ? 'Anulando…' : 'Anular'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Modal: excluir batch — pide motivo
// =============================================================================

function ModalExcluirBatch(props: {
  info: { id: number; record: number } | null;
  cerrar: () => void;
  onConfirmar: (motivo: string) => void;
  cargando: boolean;
}) {
  const [motivo, setMotivo] = useState('');
  const abierto = props.info !== null;
  return (
    <Dialog
      open={abierto}
      onOpenChange={(o) => {
        if (!o) {
          setMotivo('');
          props.cerrar();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Excluir batch de la colada</DialogTitle>
          <DialogDescription>
            El batch sigue en la base de datos como histórico, pero deja de sumar a los agregados de
            esta colada. Queda disponible para reasignar a otra colada en estado sugerida.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const limpio = motivo.trim();
            if (limpio) props.onConfirmar(limpio);
          }}
          className="grid gap-3"
        >
          {props.info && (
            <p className="text-sm text-muted-foreground">
              Batch a excluir: record <span className="font-mono">{props.info.record}</span> (id{' '}
              <span className="font-mono">{props.info.id}</span>)
            </p>
          )}
          <Label htmlFor="motivo-excluir">Motivo de la exclusión</Label>
          <Textarea
            id="motivo-excluir"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ej: operador olvidó cambiar el texto de destino, este batch va a otra colada..."
            rows={3}
            maxLength={500}
            required
          />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={props.cerrar}
              disabled={props.cargando}
            >
              <X className="h-4 w-4" /> Cancelar
            </Button>
            <Button type="submit" variant="destructive" disabled={props.cargando || !motivo.trim()}>
              <Minus className="h-4 w-4" />
              {props.cargando ? 'Excluyendo…' : 'Excluir'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Modal: agregar batch huérfano — selector de la lista de huérfanos
// =============================================================================

function ModalAgregarHuerfano(props: {
  abierto: boolean;
  cerrar: () => void;
  /** Mover el huérfano a la colada actual (otra colada de origen). */
  onConfirmar: (idBatch: number) => void;
  /** Restaurar el huérfano a su colada de origen (cuando es "esta colada"). */
  onRestaurar: (idBatch: number) => void;
  cargando: boolean;
  /** ID de la colada actual, usado para distinguir agregar vs restaurar. */
  idColadaActual: number;
}) {
  const consulta = useBatchesHuerfanos();
  const huerfanos = consulta.data?.data?.huerfanos ?? [];

  return (
    <Dialog
      open={props.abierto}
      onOpenChange={(o) => {
        if (!o) props.cerrar();
      }}
    >
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Agregar batch huérfano a esta colada</DialogTitle>
          <DialogDescription>
            Estos son los batches excluidos pendientes de reasignar. Para los que vengan de
            <strong> esta colada</strong> ("Restaurar") simplemente se deshace la exclusión. Para
            los de otras coladas ("Agregar") se mueven acá y se recalculan los agregados de ambas —
            la regla de agrupación automática NO aplica, es decisión manual.
          </DialogDescription>
        </DialogHeader>

        {consulta.isLoading && <p className="text-sm text-muted-foreground">Cargando huérfanos…</p>}
        {consulta.error && (
          <p className="text-sm text-destructive">
            Error al cargar huérfanos: {consulta.error.message}
          </p>
        )}
        {!consulta.isLoading && huerfanos.length === 0 && (
          <p className="py-6 text-center text-sm text-muted-foreground">
            No hay batches huérfanos disponibles.
          </p>
        )}
        {huerfanos.length > 0 && (
          <div className="max-h-[400px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Record</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead className="text-right">m³</TableHead>
                  <TableHead>Planta</TableHead>
                  <TableHead>Destino digitado</TableHead>
                  <TableHead>Receta</TableHead>
                  <TableHead>Origen</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {huerfanos.map((h) => (
                  <TableRow key={h.id_batch}>
                    <TableCell className="font-mono">{h.record_no}</TableCell>
                    <TableCell className="font-mono text-sm">
                      <FechaCR fecha={h.fecha_inicio} formato="corto" />
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {h.m3_producidos.toFixed(3)}
                    </TableCell>
                    <TableCell className="text-sm">
                      {nombrePlantaDisplay(h.planta_codigo)}
                    </TableCell>
                    <TableCell className="text-sm">{h.cliente_raw}</TableCell>
                    <TableCell className="text-sm">{h.recipe_name_raw ?? '—'}</TableCell>
                    <TableCell className="font-mono text-sm">
                      #{h.codigo_interno_actual}
                      {h.id_colada_actual === props.idColadaActual && (
                        <span className="ml-1 text-xs text-muted-foreground">(esta colada)</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {h.id_colada_actual === props.idColadaActual ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => props.onRestaurar(h.id_batch)}
                          disabled={props.cargando}
                          className="gap-1.5"
                          title="Restaurar a esta colada (deshacer exclusión)"
                        >
                          <Undo2 className="h-4 w-4" /> Restaurar
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => props.onConfirmar(h.id_batch)}
                          disabled={props.cargando}
                          className="gap-1.5"
                        >
                          <Plus className="h-4 w-4" /> Agregar
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={props.cerrar} disabled={props.cargando}>
            <X className="h-4 w-4" /> Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Modal: asignar obra (botón [Asignar obra] de la barra de acciones)
// =============================================================================

function ModalAsignarObra(props: {
  abierto: boolean;
  cerrar: () => void;
  valorActual: string | null;
  onConfirmar: (obraWorksNo: string | null) => void;
  cargando: boolean;
}) {
  // Estado interno para que el usuario pueda elegir y luego confirmar
  // (no se dispara la mutación con cada click en el picker).
  const [seleccion, setSeleccion] = useState<string | null>(props.valorActual);

  // Resincronizar con valorActual cada vez que se abre el modal — así si la
  // obra cambió externamente (otra pestaña, otra mutación) la selección
  // refleja el valor real.
  useEffect(() => {
    if (props.abierto) setSeleccion(props.valorActual);
  }, [props.abierto, props.valorActual]);

  const hayCambio = seleccion !== props.valorActual;

  return (
    <Dialog
      open={props.abierto}
      onOpenChange={(o) => {
        if (!o) {
          setSeleccion(props.valorActual);
          props.cerrar();
        }
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {props.valorActual ? 'Cambiar obra de la colada' : 'Asignar obra a la colada'}
          </DialogTitle>
          <DialogDescription>
            La obra (works_no) se asocia a la colada para que el Pedido de Ensamblado de Business
            Central quede ligado. Podés cambiarla o quitarla en cualquier momento mientras la colada
            no esté cerrada/anulada.
          </DialogDescription>
        </DialogHeader>

        <PickerObras valor={seleccion} onChange={setSeleccion} />

        <DialogFooter>
          <Button type="button" variant="outline" onClick={props.cerrar} disabled={props.cargando}>
            <X className="h-4 w-4" /> Cancelar
          </Button>
          <Button
            type="button"
            onClick={() => props.onConfirmar(seleccion)}
            disabled={props.cargando || !hayCambio}
            className="gap-1.5"
          >
            <Check className="h-4 w-4" />
            {props.cargando ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
